import { action, makeObservable, observable, runInAction, set } from 'mobx';
import { debounce } from 'throttle-debounce';

import { OperationAbortedError } from 'src/app/errors';
import { requireService } from 'src/packages/di';

import { equalsDeep } from '../shared/utils/equalsDeep';

import type { TAlertsGeneralSettingsData } from './AlertsGeneralSettings.api';
import { AlertsGeneralSettingsApi, getDefaultAlertsGeneralSettings } from './AlertsGeneralSettings.api';
import type { IAlertsGeneralPreferenceService } from './IAlertsGeneralPreferenceService';

export class AlertsGeneralPreferenceService implements IAlertsGeneralPreferenceService {
  private readonly api: AlertsGeneralSettingsApi;

  @observable preferences: TAlertsGeneralSettingsData;

  syncedPreferences: TAlertsGeneralSettingsData;

  @observable preferencesId: number | null = null;

  @observable isLoading = false;
  @observable isInitialized = false;

  abortController: AbortController | null = null;

  debounceSyncPreferences = debounce(300, this.syncPreferences);

  constructor(private readonly notifications = requireService('notifications')) {
    this.api = new AlertsGeneralSettingsApi();
    this.preferences = getDefaultAlertsGeneralSettings();
    this.syncedPreferences = getDefaultAlertsGeneralSettings();
    makeObservable(this);
  }

  async loadPreferences() {
    this.setLoading(true);
    try {
      const records = await this.api.loadPreferences();
      if (records.length === 0) {
        const defaults = getDefaultAlertsGeneralSettings();
        const created = await this.api.createPreferences(defaults);
        runInAction(() => {
          this.preferences = defaults;
          this.preferencesId = created.id;
          this.syncedPreferences = defaults;
        });
        return;
      }
      runInAction(() => {
        this.preferences = records[0].data;
        this.preferencesId = records[0].id;
        this.syncedPreferences = records[0].data;
      });
    } catch {
      this.notifications.showErrorMessageT('errors:failedToLoadUserPreferences');
    } finally {
      this.setLoading(false);
      this.isInitialized = true;
    }
  }

  async updatePreferences(settings: Partial<TAlertsGeneralSettingsData['settings']>) {
    set(this.preferences.settings, settings);
    this.debounceSyncPreferences();
  }

  async syncPreferences() {
    if (equalsDeep(this.preferences.settings, this.syncedPreferences.settings)) {
      return;
    }
    if (!this.preferencesId) {
      throw new Error('Preferences ID is not set');
    }
    if (this.isLoading && this.abortController && !this.abortController.signal.aborted) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const cloned = { ...this.preferences, settings: { ...this.preferences.settings } };
    this.setLoading(true);
    try {
      await this.api.updatePreferences(this.preferencesId, cloned, this.abortController);
      runInAction(() => {
        this.setLoading(false);
        this.abortController = null;
        this.syncedPreferences = cloned;
      });
    } catch (error) {
      console.error(error);
      if (error instanceof OperationAbortedError) {
        return;
      }
      this.notifications.showErrorMessageT('errors:failedToUpdateUserPreferences');
      runInAction(() => {
        this.preferences = this.syncedPreferences;
        this.setLoading(false);
      });
    }
  }

  @action
  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }
}
